import type {
  CreateUserAttendeeInput,
  UpdateUserAttendeeInput,
  UserAttendeeRow,
  UserAttendeeView,
  VersionedFormResponses,
} from '#shared/types/user-attendees'
import { userAttendeeToRow } from '#shared/types/user-attendees'
import { logApiError } from '~/utils/logger'

interface ApiResponse<T> {
  data: T | null
  error: string | null
  success: boolean
}

/**
 * Composable for managing saved user attendee profiles
 *
 * Allows parents to save their children's information for quick reuse during checkout.
 * Uses server API endpoints for consistent authentication and authorization.
 */
export function useUserAttendees() {
  /**
   * Get all saved attendees for the current user
   */
  const getAttendees = async (): Promise<ApiResponse<UserAttendeeView[]>> => {
    try {
      return await $api('/api/user/attendees', {
        method: 'GET',
      })
    }
    catch (error) {
      logApiError('/api/user/attendees', error)

      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to fetch attendees',
        success: false,
      }
    }
  }

  /**
   * Get a single attendee by ID
   */
  const getAttendeeById = async (
    id: string,
  ): Promise<ApiResponse<UserAttendeeView>> => {
    try {
      return await $api(`/api/user/attendees/${id}`, {
        method: 'GET',
      })
    }
    catch (error) {
      logApiError(`/api/user/attendees/${id}`, error, { id })

      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to fetch attendee',
        success: false,
      }
    }
  }

  /**
   * Transform camelCase input to snake_case for API
   * Filters out undefined values to avoid validation issues
   */
  const transformToSnakeCase = (
    input: CreateUserAttendeeInput | UpdateUserAttendeeInput,
  ): Partial<UserAttendeeRow> => {
    return userAttendeeToRow(input)
  }

  /**
   * Create a new saved attendee
   */
  const createAttendee = async (
    input: CreateUserAttendeeInput,
  ): Promise<ApiResponse<UserAttendeeView>> => {
    try {
      const payload = transformToSnakeCase(input)

      return await $api('/api/user/attendees', {
        method: 'POST',
        body: payload,
      })
    }
    catch (error) {
      // If it's a validation error with field details, format for user
      const errorWithStatus = error as { statusCode?: number, data?: { errors?: Array<{ field: string, message: string }> } }
      if (errorWithStatus.statusCode === 409) {
        return {
          data: null,
          error: 'DUPLICATE_RIJKREGISTER',
          success: false,
        }
      }
      if (errorWithStatus.statusCode === 400 && errorWithStatus.data?.errors) {
        const fieldErrors = errorWithStatus.data.errors

        const errorDetails = fieldErrors
          .map((e: { field: string, message: string }) => `• ${e.field}: ${e.message}`)
          .join('\n')

        logApiError('/api/user/attendees', error, { input })

        return {
          data: null,
          error: `Validatiefout:\n${errorDetails}`,
          success: false,
        }
      }

      logApiError('/api/user/attendees', error, { input })

      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to create attendee',
        success: false,
      }
    }
  }

  /**
   * Update an existing attendee
   */
  const updateAttendee = async (
    id: string,
    input: UpdateUserAttendeeInput,
  ): Promise<ApiResponse<UserAttendeeView>> => {
    try {
      return await $api(`/api/user/attendees/${id}`, {
        method: 'PATCH',
        body: transformToSnakeCase(input),
      })
    }
    catch (error) {
      logApiError(`/api/user/attendees/${id}`, error, { id, input })

      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to update attendee',
        success: false,
      }
    }
  }

  /**
   * Delete an attendee
   */
  const deleteAttendee = async (id: string): Promise<ApiResponse<void>> => {
    try {
      return await $api(`/api/user/attendees/${id}`, {
        method: 'DELETE',
      })
    }
    catch (error) {
      logApiError(`/api/user/attendees/${id}`, error, { id })

      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to delete attendee',
        success: false,
      }
    }
  }

  /**
   * Find attendee by rijksregisternummer
   * Note: Uses client-side filtering from getAttendees()
   */
  const findByRijksregister = async (
    rijksregisternummer: string,
  ): Promise<ApiResponse<UserAttendeeView | null>> => {
    // Clean the input (remove dots and dashes)
    const cleaned = rijksregisternummer.replace(/\D/g, '')

    if (cleaned.length !== 11)
      return { data: null, error: null, success: true }

    // Format it properly for matching
    const formatted = `${cleaned.slice(0, 2)}.${cleaned.slice(
      2,
      4,
    )}.${cleaned.slice(4, 6)}-${cleaned.slice(6, 9)}.${cleaned.slice(9, 11)}`

    try {
      const result = await getAttendees()
      if (!result.success || !result.data)
        return { data: null, error: result.error, success: false }

      const found = result.data.find(
        a => a.rijksregisternummer === formatted,
      )

      return { data: found || null, error: null, success: true }
    }
    catch (error) {
      logApiError('/api/user/attendees (find by rijksregister)', error, {
        rijksregisternummer,
      })

      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to find attendee',
        success: false,
      }
    }
  }

  /**
   * Find attendee by name and date of birth
   * Note: Uses client-side filtering from getAttendees()
   */
  const findByNameAndBirthday = async (
    firstName: string,
    lastName: string,
    dateOfBirth: string,
  ): Promise<ApiResponse<UserAttendeeView | null>> => {
    try {
      const result = await getAttendees()
      if (!result.success || !result.data)
        return { data: null, error: result.error, success: false }

      const found = result.data.find(
        a =>
          a.firstName.toLowerCase() === firstName.trim().toLowerCase()
          && a.lastName.toLowerCase() === lastName.trim().toLowerCase()
          && a.dateOfBirth === dateOfBirth,
      )

      return { data: found || null, error: null, success: true }
    }
    catch (error) {
      logApiError('/api/user/attendees (find by name)', error, {
        firstName,
        lastName,
        dateOfBirth,
      })

      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to find attendee',
        success: false,
      }
    }
  }

  /**
   * Save form responses for an attendee
   * Note: Form responses are saved within the saved_form_responses JSONB field
   */
  const saveFormResponses = async (
    attendeeId: string,
    formId: string,
    responses: VersionedFormResponses,
  ): Promise<ApiResponse<UserAttendeeView>> => {
    try {
      // Get current attendee
      const current = await getAttendeeById(attendeeId)
      if (!current.success || !current.data)
        return current

      // Merge form responses
      const updatedFormResponses = {
        ...current.data.savedFormResponses,
        [formId]: responses,
      }

      // Update attendee with merged responses
      return await updateAttendee(attendeeId, {
        savedFormResponses: updatedFormResponses,
      })
    }
    catch (error) {
      logApiError(
        `/api/user/attendees/${attendeeId} (save form responses)`,
        error,
        { attendeeId, formId },
      )

      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to save form responses',
        success: false,
      }
    }
  }

  /**
   * Set an attendee as primary
   */
  const setPrimary = async (id: string): Promise<ApiResponse<UserAttendeeView>> => {
    try {
      return await $api(`/api/user/attendees/${id}/primary`, {
        method: 'PATCH',
      })
    }
    catch (error) {
      logApiError(`/api/user/attendees/${id}/primary`, error, { id })

      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to set primary attendee',
        success: false,
      }
    }
  }

  return {
    getAttendees,
    getAttendeeById,
    createAttendee,
    updateAttendee,
    deleteAttendee,
    findByRijksregister,
    findByNameAndBirthday,
    saveFormResponses,
    setPrimary,
  }
}
